import { useContext } from 'react'
import { useLocation } from 'react-router-dom'

import { Helmet } from 'react-helmet-async'

import { faBell, faLocationDot } from '@fortawesome/pro-light-svg-icons'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import { generateUrls } from '@hooks/useNotification'
import useNotifications from '@hooks/useNotifications'

import { getNotifiableTypeFromAudience } from '@functions/notification'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import PageContext from '@contexts/pageContext'

import NotificationListItem from '../NotificationListItem'

const buildPageFilterFields = () => {
  const boxProps = { marginBottom: 'large' }
  const style = { marginBottom: 16 }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'select',
        style,
      },
      key: 'audience',
      label: 'Filter by Audience',
      values: [
        { key: 'global', label: 'Global' },
        { key: 'agencies', label: 'Agency' },
        { key: 'brands', label: 'Brand' },
        { key: 'providers', label: 'Provider' },
      ],
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'select',
        style,
      },
      key: 'realhub_platform',
      label: 'Filter by Realhub Platform',
      values: [
        { key: 'legacy', label: 'Legacy' },
        { key: 'standalone_v1', label: 'Standalone' },
      ],
    },
    {
      callbacks: {
        shouldRender: ({ audience }: { audience?: string }) => audience === 'agencies',
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'agencies',
      },
      key: 'notifiable_id',
    },
    {
      callbacks: {
        shouldRender: ({ audience }: { audience?: string }) => audience === 'brands',
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'brands',
      },
      key: 'notifiable_id',
    },
    {
      callbacks: {
        shouldRender: ({ audience }: { audience?: string }) => audience === 'providers',
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'providers',
      },
      key: 'notifiable_id',
    },
  ]
}

type QueryParams = { active?: 'true' | 'false', location?: 'targeted' | 'list'}
interface PageContentProps {
  pageFilters: {
    [key: string]: any,
  },
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props
  const { audience } = pageFilters

  useLocation()

  const { active, location }: QueryParams = parsePermittedQueryParams(getQueryParamsFromHash(), ['active', 'location'])
  const filterActive = active || 'true'
  const filterLocation = location || 'targeted'

  const getFilterType = (activeParam: string, locationParam: string) => {
    if (locationParam === 'list') return locationParam
    if (activeParam === 'false') return 'inactive'
    return 'active'
  }

  const filterType = getFilterType(filterActive, filterLocation)

  const notificationsPayload = useNotifications({
    filters: {
      location: filterLocation,
      notifiable_type: getNotifiableTypeFromAudience(audience),
      active: filterLocation !== 'list' ? filterActive : undefined,
      ...pageFilters,
    },
    performHttpRequests: true,
    requestOptions: { include_notification_notifiable: true },
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredNotifications,
    filteredNotificationsCount,
    hasFilteredNotifications,
    loading,
  } = notificationsPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      showCreateNotificationModal,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const { listNotificationsIndexUrl, notificationsIndexUrl, notificationInactiveUrl } = generateUrls()

  return (
    <>
      <Helmet>
        <title>Notifications | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button
            buttonStyle="primaryCreate"
            onClick={() => showCreateNotificationModal()}
            size="medium"
          >
            Create
          </Button>
        )}
        activeTabBarItemKey={filterType}
        tabBarItems={[
          {
            href: notificationsIndexUrl,
            icon: faLocationDot,
            key: 'active',
            title: 'Active',
          },
          {
            href: notificationInactiveUrl,
            icon: faLocationDot,
            key: 'inactive',
            title: 'Inactive',
          },
          {
            href: listNotificationsIndexUrl,
            icon: faBell,
            key: 'list',
            title: 'List',
          },
        ]}
        title="Notifications"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredNotifications && <ListBlankState />}

            {!loading && hasFilteredNotifications && (
              <DashboardModule contentBoxProps={{ flexDirection: 'column' }} title="Notifications">
                <Box flexDirection="column">
                  {filteredNotifications.map(notification => (
                    <NotificationListItem key={notification.id} notification={notification} />
                  ))}
                </Box>

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredNotificationsCount}
                  loading={loading}
                />
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
